/**
 * String manipulation utilities
 */

/**
 * Treats stringified missing markers ("null", "undefined") as actually missing.
 * Cached responses in IndexedDB sometimes round-trip absent fields as the
 * literal strings "null"/"undefined", which surfaces in the UI verbatim.
 */
export const coerceMissing = <T>(value: T): T | null => {
  if (value == null) return null;
  if (typeof value !== 'string') return value;
  const normalised = value.trim().toLowerCase();
  if (normalised === '' || normalised === 'null' || normalised === 'undefined') return null;
  return value;
};

/**
 * Generates a UUID-like string using cryptographically secure random values
 */
export const generateUUID = (randomSubstr: number = 4): string => {
  // a - unix timestamp
  const time = Math.round(new Date().getTime());
  const a = time.toString(36);
  // d - cryptographically secure random
  const array = new Uint8Array(Math.ceil(randomSubstr / 2));
  crypto.getRandomValues(array);
  const d = Array.from(array, (b) => b.toString(16).padStart(2, '0'))
    .join('')
    .substring(0, randomSubstr);
  return [a, d].join('');
};

/**
 * Extracts characters from a string
 */
export const extractCharacters = (str: string, charCount: number): string => {
  try {
    if (str.length === 0) {
      return str;
    }
    const extractedChars = str.slice(0, charCount);
    return extractedChars;
  } catch (error) {
    console.error('Error in extracting characters from string', error);
  }
  return str;
};

/**
 * Generates random alphanumeric string
 */
export function randomAlphanumeric(stringLength: number = 10, caps: boolean = false): string {
  const alphabets = caps ? 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' : 'abcdefghijklmnopqrstuvwxyz';
  let result = '';

  for (let i = 0; i < stringLength; i++) {
    const randomIndex = getRandomNumber(0, alphabets.length);
    result += alphabets.charAt(randomIndex);
  }

  return result;
}

function getRandomNumber(min: number, max: number): number {
  const range = max - min + 1;
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return min + (array[0]! % range);
}

/**
 * Extracts AWS account ID from ARN string
 */
export const extractAccountIdFromArn = (arnString: string): string => {
  // Regular expression to match the account ID portion of the ARN
  const accountIdRegex = /arn:aws:iam::(\d+):role/;

  // Match the ARN against the regular expression
  const match = arnString.match(accountIdRegex);

  // If a match is found, return the captured account ID
  if (match) {
    return match[1] ?? '';
  }

  // If no match is found, return an error message or null (as preferred)
  return 'Invalid ARN format or account ID not found';
};

/**
 * Creates unique copy name for duplicate items
 */
function createUniqueCopyName(names: string[], newItem: string): string {
  // If the item is not already in the list, return it as is
  if (!names.includes(newItem)) {
    return newItem;
  }

  let copySuffix = 1;
  const regex = /^(.*?)-copy\((\d+)\)$/i;

  // Extract the prefix and copy suffix from the existing name, if it has a copy suffix
  const match = newItem.match(regex);
  let baseName = newItem;
  if (match) {
    baseName = match[1] ?? newItem;
    copySuffix = parseInt(match[2] ?? '0') + 1;
  }

  // Construct the new name with the incremented copy suffix
  let newName = `${baseName}-copy(${copySuffix})`;

  // Check if the new name already exists and increment the copy suffix until a unique name is found
  while (names.includes(newName)) {
    copySuffix++;
    newName = `${baseName}-copy(${copySuffix})`;
  }

  return newName;
}

interface NamedItem {
  name: string;
  [key: string]: unknown;
}

/**
 * Gets copy tab name for duplicate operation
 */
export const getCopyTabName = (tabName: string, data: Record<string, NamedItem>): string => {
  const names = Object.values(data || {}).map((item) => item.name);
  return createUniqueCopyName(names, tabName);
};
